import toast from 'react-hot-toast'

/**
 * Utility function to handle various error response formats from backend
 * Prioritizes errors field over message field
 */
export const extractErrorMessage = (error: any, fallbackDefault = 'An error occurred'): string => {
  // Priority 1: Check for errors field (all formats)
  if (error?.response?.data?.errors) {
    const errors = error.response.data.errors;

    // Format: errors is an array
    if (Array.isArray(errors)) {
      return errors.join('\n');
    }

    // Format: errors is a string
    if (typeof errors === 'string') {
      return errors;
    }

    // Format: errors is an object (field-level or nested)
    if (typeof errors === 'object') {
      const formatted = formatValidationErrors(errors);
      if (formatted) return formatted;
    }
  }

  // Priority 2: Fall back to message field
  if (error?.response?.data?.message && typeof error.response.data.message === 'string') {
    return error.response.data.message;
  }

  // Priority 3: Fall back to error field
  if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    return error.response.data.error;
  }

  // Priority 4: Standard JS error message
  if (error?.message && typeof error.message === 'string' && error.message !== 'Network Error') {
    return error.message;
  }

  // Default fallback
  return fallbackDefault;
};

/**
 * Centralized API error toast display
 * Shows the exact backend error message if available, otherwise falls back to fallbackDefault
 */
export const handleApiError = (error: any, fallbackDefault = 'An error occurred'): string => {
  const message = extractErrorMessage(error, fallbackDefault);
  toast.error(message);
  return message;
};

/**
 * Format validation errors from backend into readable format
 * Handles nested objects recursively
 */
export const formatValidationErrors = (errors: Record<string, any>): string => {
  const errorMessages: string[] = [];

  for (const [key, value] of Object.entries(errors)) {
    if (typeof value === 'string') {
      errorMessages.push(value);
    } else if (Array.isArray(value)) {
      // Handle array of errors for a field
      errorMessages.push(...value);
    } else if (typeof value === 'object' && value !== null) {
      // Handle nested objects recursively
      const nestedErrors = formatValidationErrors(value as Record<string, any>);
      errorMessages.push(nestedErrors);
    }
  }

  return errorMessages.join('\n');
};

/**
 * Extract field-level errors for form error display
 */
export const extractFieldErrors = (error: any): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};

  if (error?.response?.data?.errors && typeof error.response.data.errors === 'object') {
    const errors = error.response.data.errors;

    for (const [key, value] of Object.entries(errors)) {
      if (typeof value === 'string') {
        fieldErrors[key] = value;
      }
    }
  }

  return fieldErrors;
};
